// src/components/PreTradeChecklistModal.jsx
import React, { useEffect, useState, useRef } from "react";
import { ListChecks, X } from "lucide-react"; 
import { CheckCircle } from "lucide-react";


export default function PreTradeChecklistModal({

  onClose,
  onConfirm,
  initialSelections = {},
  readOnly = false,          
  dateLabel = ""             
}) {
  const [isChecked, setIsChecked] = useState({
    routine: false,
    affirmations: false,
    physical: false,
    hydrated: false,
    mindset: false,
    workstation: false,
    news: false,
    research: false,
    plan: false,
    lessons: false,
    limits: false
  });

  const hydratedRef = useRef(false);

   useEffect(() => {
     if (hydratedRef.current) return;                     // only once per mount
     if (initialSelections && typeof initialSelections === "object") {
       setIsChecked(prev => ({ ...prev, ...initialSelections }));
     }
     hydratedRef.current = true;
   }, [initialSelections]);

  const allChecked = Object.values(isChecked).every(Boolean);

  function handleCheck(e) {
    if (readOnly) return; // prevent edits in read-only mode
    setIsChecked(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  }

  const FIELDS = [
  ["routine", "Routine complete"],
  ["affirmations", "Affirmations / Intentions"],
  ["physical", "Physical check (rested / fed)"],
  ["hydrated", "Hydrated"],
  ["mindset", "Calm / Focused mindset"],
  ["workstation", "Workstation ready"],
  ["news", "Checked high-impact news"],
  ["research", "Plan / Research reviewed"],
  ["plan", "Entry/Exit/Invalidation set"],
  ["lessons", "Reviewed recent lessons"],
  ["limits", "Daily/Loss limits set"],
];

const wrapClass = (name) =>
  `flex items-center gap-3 rounded-lg border p-3 transition
   ${readOnly && isChecked[name]
     ? "border-blue-500 bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
     : "border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200"}`;

const labelClass = (name) =>
  `font-medium ${readOnly && isChecked[name]
     ? "text-blue-800 dark:text-blue-100"
     : "text-gray-800 dark:text-gray-200"}`;

     const LeftToggle = ({ name }) => {
       const checked = !!isChecked[name];
       if (readOnly && checked) {
         return (
           <CheckCircle
             size={20}
             className="shrink-0 text-blue-600 dark:text-blue-400"
             aria-label="Checked"
           />
         );
       }
       return (
         <input
           type="checkbox"
           name={name}
           checked={checked}
           onChange={handleCheck}
           disabled={readOnly}
           className="h-5 w-5 rounded accent-blue-600 disabled:opacity-100 disabled:cursor-default"
         />
       );
     };



  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
  <div className="min-h-full flex items-center justify-center p-4 sm:p-6">
      <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full
                max-h-[90dvh] flex flex-col">

        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800
                flex items-center justify-between border-b border-gray-200 dark:border-gray-700
                px-6 sm:px-8 py-4">
          <div className="flex items-center gap-3">
                      <ListChecks size={28} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Pre-Trade Checklist</h2>
            {dateLabel && (
              <span className="text-normal font-semibold text-gray-600 dark:text-gray-300">
                {dateLabel}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            ✕
          </button>
        </div>


        <div className="px-6 sm:px-8 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Review and confirm each item before you begin trading.
          </p>

        <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8 text-gray-700 dark:text-gray-300">
         {/* Mind & Body Section */}
         <div className="space-y-2">
           <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-1">
             Mind & Body
           </h3>

            <label className={`${wrapClass("routine")} cursor-pointer`}>
              <input
                type="checkbox"
                name="routine"
                checked={isChecked.routine}
                onChange={handleCheck}
                disabled={readOnly}
                className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
              />
             <span className={labelClass("routine")}>Did I complete my morning routine?</span>
           </label>

           <label className={`${wrapClass("affirmations")} cursor-pointer`}>
             <input
               type="checkbox"
               name="affirmations"
               checked={isChecked.affirmations}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("affirmations")}>Did I complete my trade affirmations?</span>
           </label>

           <label className={`${wrapClass("physical")} cursor-pointer`}>
             <input
               type="checkbox"
               name="physical"
               checked={isChecked.physical}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("physical")}>Do I feel physically well and alert?</span>
           </label>

           <label className={`${wrapClass("hydrated")} cursor-pointer`}>
             <input
               type="checkbox"
               name="hydrated"
               checked={isChecked.hydrated}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("hydrated")}>Am I hydrated and ready to focus?</span>
           </label>

           <label className={`${wrapClass("mindset")} cursor-pointer`}>
             <input
               type="checkbox"
               name="mindset"
               checked={isChecked.mindset}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("mindset")}>Is my mindset clear and free from emotional bias?</span>
           </label>
         </div>

         {/* Environment & Preparation Section */}
         <div className="space-y-2">
           <h3 className="font-semibold text-blue-900 dark:text-blue-400  mb-1">
             Environment & Preparation
           </h3>

           <label className={`${wrapClass("workstation")} cursor-pointer`}>
             <input
               type="checkbox"
               name="workstation"
               checked={isChecked.workstation}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("workstation")}>Is my workstation organized and free of distractions?</span>
           </label>

           <label className={`${wrapClass("news")} cursor-pointer`}>
             <input
               type="checkbox"
               name="news"
               checked={isChecked.news}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("news")}>Have I reviewed the latest market news?</span>
           </label>

           <label className={`${wrapClass("research")} cursor-pointer`}>
             <input
               type="checkbox"
               name="research"
               checked={isChecked.research}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("research")}>Did I complete my pre-market analysis?</span>
           </label>
         </div>

         {/* Strategy & Review Section */}
         <div className="space-y-2 col-span-full mt-4 sm:mt-0">
           <h3 className="font-semibold text-blue-900 dark:text-blue-400  mb-1">
             Strategy & Review
           </h3>

           <label className={`${wrapClass("plan")} cursor-pointer`}>
             <input
               type="checkbox"
               name="plan"
               checked={isChecked.plan}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("plan")}>Have I reviewed today's trading plan and strategy?</span>
           </label>

           <label className={`${wrapClass("lessons")} cursor-pointer`}>
             <input
               type="checkbox"
               name="lessons"
               checked={isChecked.lessons}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("lessons")}>Did I review my recent trade journal for key lessons?</span>
           </label>

           <label className={`${wrapClass("limits")} cursor-pointer`}>
             <input
               type="checkbox"
               name="limits"
               checked={isChecked.limits}
               onChange={handleCheck}
               disabled={readOnly}
               className="h-5 w-5 shrink-0 rounded cursor-pointer accent-blue-600 disabled:opacity-100 disabled:cursor-default"
             />
             <span className={labelClass("limits")}>Did I set my account settings and limits for the day?</span>
           </label>
         </div>
        </div>
        </div>
        
       {/* Footer */}
               <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700
                px-6 sm:px-8 py-4 flex justify-end">

                 {readOnly ? (
                   <button onClick={onClose} className="py-2 px-6 rounded-lg font-semibold text-white bg-gray-600 hover:bg-gray-700 transition">
                     Close
                   </button>
                 ) : (
                   <button onClick={() => onConfirm(isChecked)} className="py-2 px-6 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition">
                     I'm Ready to Trade
                   </button>
                 )}
               </div>
             </div>
             </div>
           </div>
         );
       }